/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020 (original work) Open Assessment Technologies SA ;
 */

/**
 * Svelte Action to provide the drag-and-drop behaviour to elements
 * @example
 *   <div class='container'
 *     use:draganddrop={key: 'test'}
 *     on:dragStart={ handleDragStart }
 *   >
 *      <div class='draggable-source'>Drag me</div>
 *   </div>
 *
 * @param {HTMLElement} node - the target node (the container) the action applies to
 * @returns {Object} - target's lifecycle hooks
 * @fires dragStart - fires when dragging starts
 * @fires dragStop - fires when dragging stops
 * @fires update - fires when draggable changes its dropArea
 * @fires out - fires when draggable enters drop area
 * @fires over - fires when draggable leaves drop area
 */
import Draggable, { TouchSensorWithDistance } from '../draggable.js';

const draggableClassName = 'draggable-source';

let draggableInstances = new Map();

/**
 * Action makes a node into a draggable element or into a droparea.
 * Note: Pointer should not move during `delayTouch/delayMouse`, otherwise drag is not detected.
 * `distanceTouch/distanceMouse` is applied and measured only after delay exceeds.
 * With default options, drag starts after small movement for mouse, and after longpress for touch.
 * This way touchscroll is possible, and click on draggable element can be detected too.
 * @param {HTMLElement} node element which uses the action
 * @param {Object} options - the set of options
 * @param {String} options.draggableGroupKey - identifier of a group of draggables and dropAreas
 * @param {Number} options.delayTouch - the delay in millisecond between touchStart and dragStart event (250 by default)
 * @param {Number} options.delayMouse - the delay in millisecond between mouseDown and dragStart event (0 by default)
 * @param {Number} options.distanceTouch - the distance in pixels which touch pointer should travel before dragStart (0 by default)
 * @param {Number} options.distanceMouse - the distance in pixels which mouse should travel before dragStart (20 by default)
 * @param {String} options.key - the unique identifier of draggable or dropArea
 * @param {String} options.disabled - used to cancel drag:start draggable event
 * @param {Boolean} options.dropArea - the flag to identify the node as dropArea
 * @returns {Object} action object with destroy and getDraggableInstance methods
 */
export default function (node, options) {
    let draggableInstance = draggableInstances.get(options.draggableGroupKey);

    //create draggableInstance if not present yet
    if (!draggableInstance) {
        draggableInstance = createDraggableInstance();
        draggableInstances.set(options.draggableGroupKey, draggableInstance);
    }

    if (options && options.dropArea) {
        setupDropArea(node, options);
        draggableInstance.addContainer(node);
    } else {
        setupDraggable(node, options);
    }

    /**
     * Creates draggableInstance with given delay
     * @returns {Object} draggableInstance
     */
    function createDraggableInstance() {
        let initialDropAreaKey;
        let overDropAreaKey;
        let createdDraggableInstance;
        let currentDraggableKey;

        createdDraggableInstance = new Draggable([], {
            delay: {
                mouse: typeof options.delayMouse === 'number' ? options.delayMouse : 0,
                drag: 0,
                touch: typeof options.delayTouch === 'number' ? options.delayTouch : 300
            },
            distance: typeof options.distanceMouse === 'number' ? options.distanceMouse : 20,
            distanceTouch: typeof options.distanceTouch === 'number' ? options.distanceTouch : 0,
            mirror: { constrainDimensions: true },
            sensors: [TouchSensorWithDistance],
            exclude: { sensors: [Draggable.Sensors.TouchSensor] }
        });

        createdDraggableInstance.on('drag:start', e => {
            currentDraggableKey = e.originalSource.dataset.dragDropKey;
            initialDropAreaKey = e.sourceContainer.dataset.dragDropKey;
            if (e.originalSource.dataset.dragDropDisabled && e.originalSource.dataset.dragDropDisabled === 'true') {
                e.cancel();
            } else {
                e.originalSource.dispatchEvent(
                    new CustomEvent('dragStart', {
                        detail: { dropAreaKey: initialDropAreaKey, draggableKey: currentDraggableKey },
                        bubbles: true
                    })
                );
            }
        });

        createdDraggableInstance.on('drag:stop', e => {
            e.originalSource.dispatchEvent(
                new CustomEvent('dragStop', {
                    detail: { dropAreaKey: initialDropAreaKey, draggableKey: currentDraggableKey },
                    bubbles: true
                })
            );
            if (overDropAreaKey && overDropAreaKey !== initialDropAreaKey) {
                const initialDropAreaContainer = createdDraggableInstance.containers.find(
                    container => container.dataset.dragDropKey === initialDropAreaKey
                );
                const currentDropAreaContainer = createdDraggableInstance.containers.find(
                    container => container.dataset.dragDropKey === overDropAreaKey
                );
                if (
                    initialDropAreaContainer.dataset.dragDropDisabled !== 'true' &&
                    currentDropAreaContainer.dataset.dragDropDisabled !== 'true'
                ) {
                    initialDropAreaContainer.dispatchEvent(
                        new CustomEvent('update', {
                            detail: { dropAreaKey: initialDropAreaKey, draggableKey: null },
                            bubbles: true
                        })
                    );

                    currentDropAreaContainer.dispatchEvent(
                        new CustomEvent('update', {
                            detail: {
                                dropAreaKey: overDropAreaKey,
                                draggableKey: currentDraggableKey,
                                initialDropAreaKey
                            },
                            bubbles: true
                        })
                    );
                }
            }
        });

        createdDraggableInstance.on('drag:move', e => {
            const originalEvent = e.originalEvent; //pointer event (mousemove/touchmove)
            e.originalSource.dispatchEvent(
                new CustomEvent('dragMove', {
                    detail: { originalEvent, dropAreaKey: initialDropAreaKey, draggableKey: currentDraggableKey },
                    bubbles: false
                })
            );
        });

        createdDraggableInstance.on('drag:over:container', e => {
            overDropAreaKey = e.overContainer.dataset.dragDropKey;

            const overDropAreaNode = createdDraggableInstance.containers.find(
                container => container.dataset.dragDropKey === overDropAreaKey
            );
            if (overDropAreaNode && e.overContainer.dataset.dragDropDisabled !== 'true') {
                overDropAreaNode.dispatchEvent(
                    new CustomEvent('over', {
                        detail: { draggableKey: currentDraggableKey, dropAreaKey: overDropAreaKey, initialDropAreaKey },
                        bubbles: true
                    })
                );
            }
        });

        createdDraggableInstance.on('drag:out:container', e => {
            overDropAreaKey = null;

            const overDropAreaNode = createdDraggableInstance.containers.find(
                container => container.dataset.dragDropKey === e.overContainer.dataset.dragDropKey
            );

            if (overDropAreaNode && e.overContainer.dataset.dragDropDisabled !== 'true') {
                overDropAreaNode.dispatchEvent(
                    new CustomEvent('out', {
                        detail: { draggableKey: currentDraggableKey, dropAreaKey: e.overContainer.dataset.dragDropKey },
                        bubbles: true
                    })
                );
            }
        });

        return createdDraggableInstance;
    }

    return {
        update(newOptions) {
            //if key is changed update the appropriate dataset prop
            if (newOptions.key && newOptions.key !== options.key) {
                node.dataset.dragDropKey = newOptions.key;
                options.key = newOptions.key;
            }
            if (newOptions.disabled !== options.disabled) {
                node.dataset.dragDropDisabled = newOptions.disabled;
                options.disabled = newOptions.disabled;
            }
        },
        destroy() {
            if (options.dropArea) {
                const foundContainer = draggableInstance.containers.find(
                    container => container.dataset.dragDropKey === options.key
                );
                if (foundContainer) {
                    draggableInstance.removeContainer(foundContainer);
                }
            }

            if (draggableInstance.containers.length === 0) {
                draggableInstance.destroy();
                draggableInstances.delete(options.draggableGroupKey);
            }
        },
        getDraggableInstance() {
            return draggableInstance;
        }
    };
}

function setupDraggable(targetNode, options) {
    //setup classname and key for Draggable.js draggable
    targetNode.classList.add(draggableClassName);
    targetNode.dataset.dragDropKey = options.key;
    targetNode.dataset.dragDropDisabled = options.disabled ? 'true' : 'false';
}

function setupDropArea(targetNode, options) {
    //setup classname and key for Draggable.js dropzone
    targetNode.dataset.dragDropKey = options.key;
    targetNode.dataset.dragDropDisabled = options.disabled ? 'true' : 'false';
}
